import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const PLACEHOLDER_IMG = "/images/placeholder.jpg";

function resolveSponsorSrc(src) {
  if (!src) return null;
  if (src.startsWith("/") || src.startsWith("http")) return src;
  const base = process.env.PUBLIC_URL || "";
  return `${base}/${src}`.replace(/([^:]\/)\/+/g, "$1");
}

function sortSponsorsByName(list, locale) {
  return list
    .slice()
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", locale || undefined, {
        sensitivity: "base",
      })
    );
}

function SponsorsPartners() {
  const { t, i18n } = useTranslation();
  const [visibleItems, setVisibleItems] = useState([]);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sponsorsData, setSponsorsData] = useState({
    mainSponsors: [],
    goldSponsors: [],
    officialSponsors: [],
  });

  const onImgError = useCallback((e) => {
    e.target.src = `${process.env.PUBLIC_URL || ""}${PLACEHOLDER_IMG}`;
  }, []);

  useEffect(() => {
    const jsonUrl = `${process.env.PUBLIC_URL || ""}/sponsors.json`;
    fetch(jsonUrl)
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        setSponsorsData({
          mainSponsors: data.mainSponsors || [],
          goldSponsors: data.goldSponsors || [],
          officialSponsors: data.officialSponsors || [],
        });
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading sponsors:", err);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.getAttribute("data-index");
            if (index) {
              setVisibleItems((prev) =>
                prev.includes(index) ? prev : [...prev, index]
              );
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "40px" }
    );

    const observeElements = () => {
      containerRef.current?.querySelectorAll("[data-index]").forEach((el) => {
        observer.observe(el);
      });
    };

    if (!isLoading || sponsorsData.mainSponsors.length > 0 || sponsorsData.goldSponsors.length > 0 || sponsorsData.officialSponsors.length > 0) {
      const timeoutId = setTimeout(observeElements, 80);
      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    }

    return () => observer.disconnect();
  }, [sponsorsData, isLoading]);

  const sortedMainSponsors = useMemo(
    () => sortSponsorsByName(sponsorsData.mainSponsors, i18n.language),
    [sponsorsData.mainSponsors, i18n.language]
  );
  const sortedGoldSponsors = useMemo(
    () => sortSponsorsByName(sponsorsData.goldSponsors, i18n.language),
    [sponsorsData.goldSponsors, i18n.language]
  );
  const sortedOfficialSponsors = useMemo(
    () => sortSponsorsByName(sponsorsData.officialSponsors, i18n.language),
    [sponsorsData.officialSponsors, i18n.language]
  );

  const revealClass = (keyId) => {
    const isVisible = visibleItems.includes(keyId);
    return [
      "motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out",
      isVisible
        ? "opacity-100 translate-y-0"
        : "motion-safe:translate-y-3 motion-safe:opacity-0",
      "motion-reduce:translate-y-0 motion-reduce:opacity-100",
    ].join(" ");
  };

  const partnerLinkAria = (name) =>
    `${name} (${t("sponsors_partners.opens_new_tab")})`;

  const renderMainSponsors = (list) => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="flex min-h-[12rem] animate-pulse flex-col items-center justify-center gap-4 rounded-2xl border border-slate-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="h-28 w-28 rounded-2xl bg-slate-200 dark:bg-slate-700" />
              <div className="h-5 w-40 rounded bg-slate-200 dark:bg-slate-700" />
            </div>
          ))}
        </div>
      );
    }

    return (
      <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-2 md:gap-6">
        {list.map((sponsor, index) => {
          const keyId = `main-${index}`;
          return (
            <li key={keyId} className="min-h-0">
              <a
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                data-index={keyId}
                style={{ transitionDelay: `${index * 70}ms` }}
                aria-label={partnerLinkAria(sponsor.name)}
                className={`group flex min-h-[12rem] flex-col items-center justify-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-amber-400 bg-white p-6 shadow-sm ring-1 ring-slate-200/50 transition-colors dark:border-slate-700 dark:border-l-amber-500 dark:bg-slate-800 dark:ring-slate-700/50 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md dark:motion-safe:hover:border-slate-600 ${revealClass(keyId)} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/35 dark:focus-visible:ring-offset-slate-900 sm:p-8`}
              >
                <div className="flex h-28 w-full max-w-[10rem] flex-shrink-0 items-center justify-center sm:h-32 sm:max-w-[11rem]">
                  <img
                    src={resolveSponsorSrc(sponsor.src)}
                    alt=""
                    className="max-h-full max-w-full object-contain motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-[1.03]"
                    onError={onImgError}
                    decoding="async"
                  />
                </div>
                <span className="text-center text-lg font-semibold tracking-tight text-slate-900 transition-colors group-hover:text-primary dark:text-slate-100 dark:group-hover:text-primary-light">
                  {sponsor.name}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderPremiumSponsors = (list) => {
    if (isLoading) {
      return (
        <ul className="m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="flex h-full min-h-0">
              <div className="flex h-full min-h-[10.5rem] w-full animate-pulse flex-col rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex flex-1 flex-col items-center justify-center gap-3">
                  <div className="h-16 w-16 rounded-xl bg-slate-200 dark:bg-slate-700" />
                  <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-700" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul className="m-0 grid list-none grid-cols-2 items-stretch gap-4 p-0 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4">
        {list.map((sponsor, index) => {
          const keyId = `gold-${index}`;
          return (
            <li key={keyId} className="flex h-full min-h-0">
              <a
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                data-index={keyId}
                style={{ transitionDelay: `${index * 55}ms` }}
                aria-label={partnerLinkAria(sponsor.name)}
                className={`group flex h-full min-h-[10.5rem] w-full flex-col rounded-2xl border border-slate-200 border-l-4 border-l-amber-500/90 bg-white p-5 shadow-sm ring-1 ring-slate-200/40 transition-colors dark:border-slate-700 dark:border-l-amber-400/80 dark:bg-slate-800 dark:ring-slate-700/40 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md ${revealClass(keyId)} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/35 dark:focus-visible:ring-offset-slate-900`}
              >
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center sm:h-[4.5rem] sm:w-[4.5rem]">
                    <img
                      src={resolveSponsorSrc(sponsor.src)}
                      alt=""
                      className="max-h-full max-w-full object-contain motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:scale-[1.03]"
                      onError={onImgError}
                      decoding="async"
                    />
                  </div>
                  <span className="line-clamp-2 w-full shrink-0 text-center text-sm font-semibold leading-snug text-slate-900 transition-colors group-hover:text-primary dark:text-slate-100 dark:group-hover:text-primary-light">
                    {sponsor.name}
                  </span>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderOfficialSponsors = (list) => {
    if (isLoading) {
      return (
        <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <li key={i}>
              <div className="flex animate-pulse items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 dark:border-slate-700 dark:bg-slate-800">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-slate-200 dark:bg-slate-700" />
                <div className="h-4 flex-1 rounded bg-slate-200 dark:bg-slate-700" />
              </div>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul
        className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
        aria-label={t("sponsors_partners.official_list_label")}
      >
        {list.map((s, index) => {
          const keyId = `official-${index}`;
          return (
            <li key={`${s.name}-${index}`} className="min-h-0">
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-index={keyId}
                style={{ transitionDelay: `${(index % 8) * 40}ms` }}
                aria-label={partnerLinkAria(s.name)}
                className={`group flex items-center gap-3 rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white px-4 py-3.5 shadow-sm ring-1 ring-slate-200/40 transition-colors dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800 dark:ring-slate-700/40 motion-safe:hover:border-slate-300 motion-safe:hover:bg-slate-50 dark:motion-safe:hover:border-slate-600 dark:motion-safe:hover:bg-slate-700/35 ${revealClass(keyId)} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/35 dark:focus-visible:ring-offset-slate-900`}
              >
                {s.src ? (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 dark:border-slate-600 dark:bg-slate-700/80">
                    <img
                      src={resolveSponsorSrc(s.src)}
                      alt=""
                      className="max-h-full max-w-full object-contain"
                      onError={onImgError}
                      decoding="async"
                    />
                  </div>
                ) : (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-700/50">
                    <i
                      className="fas fa-building text-base text-slate-400 dark:text-slate-500"
                      aria-hidden
                    />
                  </div>
                )}
                <span className="min-w-0 flex-1 text-pretty text-sm font-medium leading-snug text-slate-800 dark:text-slate-200">
                  {s.name}
                </span>
                <i
                  className="fas fa-external-link-alt shrink-0 text-xs text-primary opacity-70 dark:text-primary-light sm:opacity-40 sm:group-hover:opacity-100"
                  aria-hidden
                />
              </a>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="sponsors"
      aria-labelledby="sponsors-partners-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="sponsors-partners-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("sponsors_partners.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("sponsors_partners.subheadline")}
          </p>
          <p className="mx-auto mt-4 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("sponsors_partners.main_text")}
          </p>
        </header>

        <div ref={containerRef} className="space-y-12 md:space-y-14">
          {(sortedMainSponsors.length > 0 || isLoading) && (
            <section aria-labelledby="sponsors-tier-main-heading">
              <div className="mb-6 flex items-center gap-3 md:mb-8">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/35"
                  aria-hidden
                >
                  <i className="fas fa-trophy text-lg text-amber-700 dark:text-amber-400" />
                </div>
                <h3
                  id="sponsors-tier-main-heading"
                  className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 md:text-2xl"
                >
                  {t("sponsors_partners.main_title")}
                </h3>
              </div>
              {renderMainSponsors(sortedMainSponsors)}
            </section>
          )}

          {(sortedGoldSponsors.length > 0 || isLoading) && (
            <section aria-labelledby="sponsors-tier-premium-heading">
              <div className="mb-6 flex items-center gap-3 md:mb-8">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/35"
                  aria-hidden
                >
                  <i className="fas fa-medal text-lg text-amber-700 dark:text-amber-400" />
                </div>
                <h3
                  id="sponsors-tier-premium-heading"
                  className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 md:text-2xl"
                >
                  {t("sponsors_partners.premium_title")}
                </h3>
              </div>
              {renderPremiumSponsors(sortedGoldSponsors)}
            </section>
          )}

          {(sortedOfficialSponsors.length > 0 || isLoading) && (
            <section aria-labelledby="sponsors-tier-official-heading">
              <div className="mb-6 flex items-center gap-3 md:mb-8">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 dark:bg-primary-light/15"
                  aria-hidden
                >
                  <i className="fas fa-handshake text-lg text-primary dark:text-primary-light" />
                </div>
                <h3
                  id="sponsors-tier-official-heading"
                  className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100 md:text-2xl"
                >
                  {t("sponsors_partners.official_title")}
                </h3>
              </div>
              {renderOfficialSponsors(sortedOfficialSponsors)}
            </section>
          )}
        </div>
      </div>
    </section>
  );
}

export default SponsorsPartners;
